import React, {useEffect, useState} from "react";
import {Form, Modal} from "react-bootstrap";
import {useFormik} from "formik";
import {recordOrgPaymentBatch} from "../../generic/crud";
import {showToast, TOAST_FAILURE, TOAST_SUCCESS} from "../../../utils/toast/Toast";

const MONTH_FULL_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function formatMonthLabel(monthKey) {
    const [year, month] = monthKey.split('-');
    const monthIdx = parseInt(month, 10) - 1;
    return `${MONTH_FULL_NAMES[monthIdx]} ${year}`;
}

export function RecordPaymentDialog(props) {
    const {show, handleClose, orgId, monthlyAmount, selectedMonths, onSave} = props;
    const [isLoading, setIsLoading] = useState(false);

    const getDefaultValues = () => ({
        amount: monthlyAmount || 0,
        referenceNumber: "",
        remarks: "",
    });

    const [initialValues, setInitialValues] = useState(getDefaultValues());

    const formik = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validate: (values) => {
            const errors = {};
            if (!values.amount || values.amount <= 0) {
                errors.amount = "Amount must be greater than 0";
            }
            return errors;
        },
        onSubmit(values, {resetForm}) {
            setIsLoading(true);
            const batchData = {
                months: selectedMonths,
                amount: values.amount,
                referenceNumber: values.referenceNumber,
                remarks: values.remarks,
            };
            recordOrgPaymentBatch(orgId, batchData).then(() => {
                setIsLoading(false);
                handleClose();
                showToast(selectedMonths.length + " payment(s) recorded!", TOAST_SUCCESS);
                resetForm();
                if (onSave) onSave();
            }).catch((err) => {
                setIsLoading(false);
                showToast(err.response?.data || "Error recording payments.", TOAST_FAILURE);
            });
        }
    });

    useEffect(() => {
        setInitialValues(getDefaultValues());
    }, [show, monthlyAmount]);

    const sortedMonths = selectedMonths ? [...selectedMonths].sort() : [];
    const totalAmount = (formik.values.amount || 0) * sortedMonths.length;

    return (
        <Modal
            size="lg"
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Record Payment</Modal.Title>
            </Modal.Header>
            <Modal.Body className="overlay">
                {!isLoading && (
                    <Form className="form form-label-right">
                        {/* Selected Months Summary */}
                        <div className="mb-5">
                            <Form.Label className="fw-bold fs-6">Paying for {sortedMonths.length} month{sortedMonths.length > 1 ? 's' : ''}</Form.Label>
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                {sortedMonths.map(m => (
                                    <span key={m} className="badge badge-light-primary fs-7 px-3 py-2">
                                        {formatMonthLabel(m)}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="separator my-5"></div>

                        <div className="form-group row">
                            <Form.Group className="col-lg-6 mb-5">
                                <Form.Label className="required">Amount per Month (₹)</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    id="amount"
                                    placeholder="Enter amount per month"
                                    value={formik.values.amount}
                                    onChange={formik.handleChange}
                                    isInvalid={formik.touched.amount && !!formik.errors.amount}
                                />
                                {formik.touched.amount && formik.errors.amount && (
                                    <div className="text-danger fs-8 mt-1">{formik.errors.amount}</div>
                                )}
                            </Form.Group>

                            <Form.Group className="col-lg-6 mb-5">
                                <Form.Label>Total Amount</Form.Label>
                                <div className="form-control bg-light-primary border-0 fw-bold fs-4 text-primary">
                                    ₹{totalAmount.toLocaleString()}
                                </div>
                                <Form.Text className="text-muted">
                                    ₹{(formik.values.amount || 0).toLocaleString()} x {sortedMonths.length} month{sortedMonths.length > 1 ? 's' : ''}
                                </Form.Text>
                            </Form.Group>
                        </div>

                        <div className="form-group row">
                            <Form.Group className="col-lg-6 mb-5">
                                <Form.Label>Reference Number</Form.Label>
                                <Form.Control
                                    type="text"
                                    id="referenceNumber"
                                    placeholder="Enter reference / transaction number"
                                    value={formik.values.referenceNumber}
                                    onChange={formik.handleChange}
                                />
                            </Form.Group>

                            <Form.Group className="col-lg-6 mb-5">
                                <Form.Label>Remarks</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    id="remarks"
                                    placeholder="Enter any remarks"
                                    value={formik.values.remarks}
                                    onChange={formik.handleChange}
                                />
                            </Form.Group>
                        </div>
                    </Form>
                )}
                {isLoading && <span>Please Wait...</span>}
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-light btn-elevate"
                    onClick={handleClose}>
                    Cancel
                </button>
                <button
                    type="submit"
                    onClick={formik.handleSubmit}
                    className="btn btn-primary btn-elevate">
                    Record {sortedMonths.length} Payment{sortedMonths.length > 1 ? 's' : ''}
                </button>
            </Modal.Footer>
        </Modal>
    );
}
